import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Star, Send, Loader2, AlertTriangle, FlaskConical } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SUBJECT = "Thank you for choosing The Better Fare";

// ── Inline review history ─────────────────────────────────────────────────────
function ReviewHistory({ sourceType, sourceId, refreshTick }) {
  const { getAuthHeaders } = useAuth();
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!sourceId) return;
    axios
      .get(`${API}/review-requests`, {
        params: { source_type: sourceType, source_id: sourceId },
        headers: getAuthHeaders(),
      })
      .then((r) => setHistory(r.data || []))
      .catch(() => {});
  }, [sourceType, sourceId, refreshTick]);

  if (history.length === 0) return null;

  return (
    <div className="mt-3 space-y-1">
      {history.map((r) => (
        <div key={r.id} className="flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
          <Star className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <span className="font-medium text-slate-700">Review request sent</span>
            {r.sent_at && (
              <span className="ml-1.5">
                {format(new Date(r.sent_at), "d MMM yyyy")}
              </span>
            )}
            {r.customer_email && (
              <span className="ml-1.5 truncate">→ {r.customer_email}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ReviewRequestButton({
  sourceType,   // "booking" | "invoice" | "visa_quote" | "service_entry"
  sourceId,
  customerName = "",
  customerEmail = "",
  buttonVariant = "outline",
  buttonSize = "sm",
  className = "",
}) {
  const { getAuthHeaders } = useAuth();
  const [open,        setOpen]        = useState(false);
  const [email,       setEmail]       = useState("");
  const [subject,     setSubject]     = useState(DEFAULT_SUBJECT);
  const [sending,     setSending]     = useState(false); // false | "test" | "customer"
  const [reviewUrl,   setReviewUrl]   = useState(null); // null = loading, "" = not configured
  const [refreshTick, setRefreshTick] = useState(0);

  // Fetch google_review_url from settings when modal opens
  useEffect(() => {
    if (!open) return;
    setEmail(customerEmail || "");
    setSubject(DEFAULT_SUBJECT);
    axios
      .get(`${API}/admin/payment-settings`, { headers: getAuthHeaders() })
      .then((r) => setReviewUrl(r.data.google_review_url || ""))
      .catch(() => setReviewUrl(""));
  }, [open]);

  const previewHtml = reviewUrl
    ? `Dear ${customerName || "Customer"},\n\nThank you for choosing The Better Fare.\n\nWe hope you were satisfied with our service.\n\nIf you have a minute, we would greatly appreciate a review on Google:\n\n${reviewUrl}\n\nYour feedback helps us improve and helps other travellers find us.\n\nThank you for your trust and support.\n\nKind regards,\nThe Better Fare`
    : "";

  // Direct admin preview — no modal, fires immediately
  const handleTestSend = async () => {
    setSending("test");
    try {
      const res = await axios.post(
        `${API}/review-requests/send`,
        {
          source_type:   sourceType,
          source_id:     sourceId,
          customer_name: customerName,
          subject:       DEFAULT_SUBJECT,
          is_test:       true,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || "Admin preview sent!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send preview");
    } finally {
      setSending(false);
    }
  };

  const handleSend = async () => {
    if (!email.trim()) { toast.error("Enter a recipient email"); return; }
    setSending("customer");
    try {
      const res = await axios.post(
        `${API}/review-requests/send`,
        {
          source_type:    sourceType,
          source_id:      sourceId,
          customer_name:  customerName,
          customer_email: email.trim(),
          subject:        subject.trim() || DEFAULT_SUBJECT,
          is_test:        false,
        },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || "Review request sent!");
      setOpen(false);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send review request");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size={buttonSize}
          onClick={handleTestSend}
          disabled={!!sending}
          title="Send [TEST] preview to admin — does not notify customer or log"
          className="border-slate-300 text-slate-500 hover:bg-slate-50"
        >
          {sending === "test" ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <FlaskConical className="w-3.5 h-3.5 mr-1.5" />
          )}
          Admin Preview
        </Button>
        <Button
          variant={buttonVariant}
          size={buttonSize}
          onClick={() => setOpen(true)}
          disabled={!!sending}
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
        >
          <Star className="w-3.5 h-3.5 mr-1.5" />
          Send Review Request
        </Button>
      </div>

      <ReviewHistory sourceType={sourceType} sourceId={sourceId} refreshTick={refreshTick} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500" />
              Send Review Request
            </DialogTitle>
          </DialogHeader>

          {reviewUrl === null ? (
            <div className="py-6 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            </div>
          ) : reviewUrl === "" ? (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Google Review URL has not been configured.</p>
                <p className="text-xs mt-0.5 text-amber-700">
                  Go to <strong>Settings → Payment Settings</strong> and add your Google Review link.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 mt-1">
              <div>
                <Label className="text-xs text-slate-500">Customer Email</Label>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="customer@email.com"
                  className="mt-1 h-9"
                  autoFocus={!email}
                />
                {!customerEmail && (
                  <p className="text-xs text-amber-600 mt-1">No email found on this record — please enter manually.</p>
                )}
              </div>

              <div>
                <Label className="text-xs text-slate-500">Subject</Label>
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="mt-1 h-9"
                />
              </div>

              <div>
                <Label className="text-xs text-slate-500">Email Preview</Label>
                <pre className="mt-1 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-3 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto font-sans">
                  {previewHtml}
                </pre>
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  onClick={handleSend}
                  disabled={!!sending || !email.trim()}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {sending === "customer" ? (
                    <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-3.5 h-3.5 mr-2" />
                  )}
                  {sending === "customer" ? "Sending…" : `Send to ${email || "…"}`}
                </Button>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
